import type { WeekStartDay } from '~/models/settings'
import { type CalendarDate, toCalendarDate, unwrapDate } from '~/types/dates'

/**
 * Calendar-date arithmetic (D-034).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Every operation here works in UTC, deliberately.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * A `CalendarDate` has no time and no zone — it is the day the user recorded
 * something. Doing arithmetic on it with LOCAL `Date` methods would break twice
 * a year: adding one day across a daylight-saving boundary can land on the same
 * day or skip one entirely, which would silently shift a trailing window.
 *
 * Anchoring at UTC midnight makes every day exactly 86,400,000 ms, so the
 * arithmetic is exact.
 *
 * The one place local time is correct is deciding which day it is *now* for the
 * user — `calendarDateFrom` in `types/dates.ts` — and that happens before any
 * value reaches this module.
 */

const MS_PER_DAY = 86_400_000

function toUtcMillis(date: CalendarDate): number {
  return Date.parse(`${unwrapDate(date)}T00:00:00.000Z`)
}

function fromUtcMillis(millis: number): CalendarDate {
  const instant = new Date(millis)
  const year = String(instant.getUTCFullYear()).padStart(4, '0')
  const month = String(instant.getUTCMonth() + 1).padStart(2, '0')
  const day = String(instant.getUTCDate()).padStart(2, '0')
  const result = toCalendarDate(`${year}-${month}-${day}`)
  if (result === undefined) {
    throw new Error(`calendar arithmetic produced an invalid date: ${year}-${month}-${day}`)
  }
  return result
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  return fromUtcMillis(toUtcMillis(date) + days * MS_PER_DAY)
}

/** Signed whole days from `from` to `to`. Negative when `to` precedes `from`. */
export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  return Math.round((toUtcMillis(to) - toUtcMillis(from)) / MS_PER_DAY)
}

/** Day of week, 0 = Sunday … 6 = Saturday, matching `Date.prototype.getDay`. */
export function dayOfWeek(date: CalendarDate): number {
  return new Date(toUtcMillis(date)).getUTCDay()
}

export interface DateWindow {
  readonly from: CalendarDate
  readonly to: CalendarDate
}

/**
 * The `lengthDays` calendar days ending on `reference`, inclusive.
 *
 * A seven-day window ending on the 6th therefore covers the 31st to the 6th —
 * seven days, not eight. Off-by-one here would quietly change every average.
 */
export function trailingWindow(reference: CalendarDate, lengthDays: number): DateWindow {
  if (lengthDays < 1) throw new Error('trailingWindow needs a length of at least one day')
  return { from: addDays(reference, -(lengthDays - 1)), to: reference }
}

/** The window of the same length immediately preceding `window`. */
export function precedingWindow(window: DateWindow, lengthDays: number): DateWindow {
  const to = addDays(window.from, -1)
  return { from: addDays(to, -(lengthDays - 1)), to }
}

export function isWithin(date: CalendarDate, window: DateWindow): boolean {
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  const value = unwrapDate(date)
  return value >= unwrapDate(window.from) && value <= unwrapDate(window.to)
}

/**
 * The start of the calendar week containing `date` (D-034).
 *
 * Used for review cadence and export scope. Distinct from a phase week, which
 * is anchored to the phase start date — the two must never be conflated.
 */
export function startOfCalendarWeek(date: CalendarDate, weekStartDay: WeekStartDay): CalendarDate {
  const offset = (dayOfWeek(date) - weekStartDay + 7) % 7
  return addDays(date, -offset)
}

/**
 * The week start actually in force on a given day (D-105 §8).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONE FUNCTION, BECAUSE HALF AN AGREEMENT IS WORSE THAN EITHER ANSWER
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Every reader goes through this: the weekly review, the checklist, nutrition
 *  windows, adherence, routine-anchor cadences, `nextCommitmentEffectiveDate`,
 *  Today and materialisation. **No service may read the newly chosen value while
 *  training reads the materialised record** — a boundary that half the app agrees
 *  with is worse than either answer alone, because every symptom of it looks like
 *  a bug somewhere else.
 *
 *  **Resolution is by date, never by whether a promotion has run.** If the app is
 *  not opened on the effective date, the correct answer must still come out the
 *  next time it is asked, so nothing here depends on a write having happened.
 *  Promoting the value into `weekStartDay` is tidying, not semantics.
 */
export function weekStartDayInForce(
  settings: {
    readonly weekStartDay: WeekStartDay
    readonly pendingWeekStartDay?: {
      readonly value: WeekStartDay
      readonly effectiveFrom: CalendarDate
    }
  },
  today: CalendarDate,
): WeekStartDay {
  const pending = settings.pendingWeekStartDay
  if (pending === undefined) return settings.weekStartDay
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  return unwrapDate(today) >= unwrapDate(pending.effectiveFrom)
    ? pending.value
    : settings.weekStartDay
}

/**
 * The first day a newly chosen week start may apply to.
 *
 * The start of the week *after* the one running now, measured with the day in
 * force **now** — using the new value here would compute a boundary inside the
 * current week and defeat the whole point of deferring it.
 */
export function weekStartDayEffectiveDate(
  today: CalendarDate,
  inForce: WeekStartDay,
): CalendarDate {
  return addDays(startOfCalendarWeek(today, inForce), 7)
}

/**
 * Inclusive phase week (D-034): the phase start date is week 1, day 1.
 *
 * Returns 0 or less for dates before the phase began, which lets a caller
 * distinguish "not yet started" from "week 1".
 */
export function phaseWeekNumber(phaseStart: CalendarDate, date: CalendarDate): number {
  const elapsed = daysBetween(phaseStart, date)
  return Math.floor(elapsed / 7) + 1
}
